import React, { useCallback, useEffect, useState } from 'react';
import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Loader from '@amzn/meridian/loader';
import Select, { SelectOption } from '@amzn/meridian/select';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import Toggle from '@amzn/meridian/toggle';
import { useAppSelector } from '../../../store';
import { selectStationCode } from '../../../store/slices/shiftPlanSlice';
import { getProcessFlowMapData, getProcessFlowMetricsData } from './api';
import { GetProcessFlowMapDataOutput, GetProcessFlowMetricsDataOutput } from './model';
import { MetricInterval } from './constants';
import AndonCardsView from './AndonCardsView';
import VolumeFlowMapView from './VolumeFlowMapView';
import SupportPathsSection from './SupportPathsSection';
import { DEFAULT_METRIC_INTERVAL, METRIC_INTERVAL_OPTIONS } from './utils';

/** Which of the two views the Volume Flow toggle is showing. */
type FlowMapView = 'andon' | 'flow';

/**
 * Map type in the segmented control.
 *
 * `mheRouting` is a future capability with no data behind it, so its option is disabled
 * and this can only ever hold `processFlow`.
 */
type FlowMapMode = 'processFlow' | 'mheRouting';

/** The outcome of one endpoint's fetch: a value, or the error it failed with. */
type Settled<T> = { ok: true; value: T } | { ok: false; error: Error };

/**
 * Turns a rejection into a resolved `Settled` outcome, so the two fetches can be awaited
 * together without either one's failure discarding the other's result.
 *
 * Hand-rolled rather than `Promise.allSettled`: that is ES2020 and this package compiles
 * against `lib: ["dom", "es2015"]`.
 */
function settle<T>(promise: Promise<T>): Promise<Settled<T>> {
    return promise.then(
        (value): Settled<T> => ({ ok: true, value }),
        (error): Settled<T> => ({ ok: false, error: error as Error })
    );
}

/**
 * The RCC Visual Flow Map page.
 *
 * Two views behind the Volume Flow toggle over the same per-path data:
 *  - toggle off — Andon cards: a tile per dimension (productivity, staffing, flow, buffer)
 *    and a section per dimension listing the paths that need attention.
 *  - toggle on — the Volume Flow map, the same paths as a left-to-right directed flow.
 *
 * Buffers have a page of their own (see BuffersGated). There is no
 * in-page title or subtitle, since the SCC/Boson shell iframe supplies the page title.
 *
 * Support (indirect) paths are headcount-only and are not flow-map nodes. On the Andon view
 * they sit in the Staffing section; under the map they get a section of their own.
 *
 * Both endpoints are fetched in parallel — the topology/edges from getProcessFlowMapData
 * and the cards/Andon board from getProcessFlowMetricsData — because the toggle switches
 * instantly between views over one snapshot. Failures are independent (see `settle`), so
 * one endpoint going down degrades a single view instead of blanking the page; the error
 * state only appears when both fail.
 *
 * Buffer status arrives on each card's BUFFER metric; the thresholds behind it are
 * server-side config and are not part of the contract.
 */
const FlowMap: React.FC = () => {
    const stationCode = useAppSelector(selectStationCode);

    const [view, setView] = useState<FlowMapView>('andon');
    // The segmented control is a controlled Meridian component, so it needs both a value
    // and an onChange. MHE Routing is disabled, so this can only ever hold 'processFlow',
    // and nothing below reads it — the disabled option cannot change what renders.
    const [mode, setMode] = useState<FlowMapMode>('processFlow');
    const [selectedInterval, setSelectedInterval] = useState<MetricInterval>(DEFAULT_METRIC_INTERVAL);
    // 'idle' covers the pre-first-fetch window, so the empty state cannot flash before
    // the first response has had a chance to land.
    const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
    const [mapData, setMapData] = useState<GetProcessFlowMapDataOutput | null>(null);
    const [metricsData, setMetricsData] = useState<GetProcessFlowMetricsDataOutput | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const fetchFlowMap = useCallback(
        async (warehouseId: string, metricInterval: MetricInterval, isCurrent: () => boolean) => {
            setStatus('loading');
            setErrorMessage(null);
            const request = { warehouseId, interval: metricInterval };

            const [mapResult, metricsResult] = await Promise.all([
                settle(getProcessFlowMapData(request)),
                settle(getProcessFlowMetricsData(request)),
            ]);

            // The interval/station may have changed (or the page unmounted) while the
            // requests were in flight — drop a stale response rather than render it.
            if (!isCurrent()) return;

            setMapData(mapResult.ok ? mapResult.value : null);
            setMetricsData(metricsResult.ok ? metricsResult.value : null);

            // One endpoint failing degrades a single view; only a total failure is an error.
            if (!mapResult.ok && !metricsResult.ok) {
                setErrorMessage(mapResult.error?.message || 'Failed to load the flow map.');
                setStatus('error');
                return;
            }
            setStatus('ready');
        },
        []
    );

    useEffect(() => {
        // Deselecting the warehouse has to clear the snapshot, not just stop fetching.
        // Bailing out with the state left at 'ready' would render the "select a warehouse"
        // alert on top of the previous station's flow map — two contradictory states at
        // once, and stale data attributed to no station at all. Back to 'idle' rather than
        // 'loading', because nothing is in flight.
        if (!stationCode) {
            setStatus('idle');
            setMapData(null);
            setMetricsData(null);
            setErrorMessage(null);
            return;
        }
        let current = true;
        fetchFlowMap(stationCode, selectedInterval, () => current);
        return () => {
            current = false;
        };
    }, [fetchFlowMap, stationCode, selectedInterval]);

    // Cards drive the Andon view, nodes drive the map. They are the same per-path data
    // from two endpoints, so either one can stand in when the other's fetch failed.
    const cards = metricsData?.cards ?? mapData?.nodes;
    const nodes = mapData?.nodes ?? metricsData?.cards;
    const supportPaths = metricsData?.supportPaths ?? mapData?.supportPaths;

    const hasAnyData =
        (cards?.length ?? 0) > 0 ||
        (nodes?.length ?? 0) > 0 ||
        (supportPaths?.length ?? 0) > 0 ||
        metricsData?.andonSummary != null;

    return (
        <Column spacing="500" spacingInset="500" data-testid="flow-map-page">
            <Row spacing="500" alignmentVertical="bottom" wrap="down">
                <ButtonGroup
                    data-testid="flow-map-mode-toggle"
                    legend="Flow map type"
                    value={mode}
                    onChange={(value: string) => setMode(value as FlowMapMode)}
                >
                    <ButtonOption value="processFlow">Process Flow</ButtonOption>
                    {/* Future capability, no data behind it — genuinely disabled, not just styled. */}
                    <ButtonOption value="mheRouting" disabled={true}>MHE Routing</ButtonOption>
                </ButtonGroup>
                <Toggle
                    data-testid="flow-map-volume-flow-toggle"
                    checked={view === 'flow'}
                    onChange={(checked: boolean) => setView(checked ? 'flow' : 'andon')}
                    // Meridian labels the switch with its children; the aria-label pins the
                    // accessible name to the input itself. Keep the two strings identical.
                    aria-label="Volume Flow"
                >
                    Volume Flow
                </Toggle>
                <Column spacing="300">
                    <Text tag="label" type="b400">Interval</Text>
                    <Select
                        data-testid="flow-map-interval-select"
                        value={selectedInterval}
                        onChange={(value: MetricInterval) => setSelectedInterval(value)}
                        width={220}
                        size="large"
                    >
                        {METRIC_INTERVAL_OPTIONS.map((option) => (
                            <SelectOption key={option.value} value={option.value} label={option.label} />
                        ))}
                    </Select>
                </Column>
            </Row>

            {!stationCode && (
                <Alert type="neutral" data-testid="flow-map-no-station">
                    Select a warehouse to see the flow map.
                </Alert>
            )}

            {status === 'loading' && (
                <Column alignmentHorizontal="center" spacing="300" spacingInset="500">
                    <Loader size="large" data-testid="flow-map-loader" />
                    <Text type="b300" color="secondary">Loading flow map…</Text>
                </Column>
            )}

            {status === 'error' && (
                <Alert type="error" data-testid="flow-map-error">
                    {errorMessage}
                </Alert>
            )}

            {status === 'ready' && !hasAnyData && (
                <Alert type="neutral" data-testid="flow-map-empty">
                    No flow map data available for this interval yet.
                </Alert>
            )}

            {status === 'ready' && hasAnyData && (
                <Column spacing="500">
                    {view === 'andon' ? (
                        <AndonCardsView
                            cards={cards}
                            andonSummary={metricsData?.andonSummary}
                            supportPaths={supportPaths}
                        />
                    ) : (
                        <>
                            <VolumeFlowMapView nodes={nodes} edges={mapData?.edges} />
                            <SupportPathsSection supportPaths={supportPaths} />
                        </>
                    )}
                </Column>
            )}
        </Column>
    );
};

export default FlowMap;
